/**
 * Account data generator
 * Generates account data based on aggregated mock data
 */

import { faker } from '@faker-js/faker'
import type {
  Account,
  AccountStatistics,
  AccountBilling,
  AccountUsage,
  AccountLimits,
  AccountApiKey,
  AccountTeamMember,
  AccountIntegration,
  AccountCompliance,
  AccountSettings,
  SubscriptionPlan
} from '../../shared/types/account'

// Configuration
const ACCOUNT_CONFIG = {
  SEED: 12345,
  PLAN: 'professional' as SubscriptionPlan
}

// Plan limits
const PLAN_LIMITS: Record<SubscriptionPlan, AccountLimits> = {
  starter: {
    maxTransactionsPerMonth: 1000,
    maxVolumePerMonth: 100000,
    maxMerchants: 5,
    maxWallets: 20,
    maxApiCallsPerMonth: 10000,
    maxUsersPerAccount: 3,
    features: {
      customBranding: false,
      webhooks: true,
      advancedAnalytics: false,
      dedicatedSupport: false,
      slaGuarantee: false,
      customIntegrations: false
    }
  },
  professional: {
    maxTransactionsPerMonth: 10000,
    maxVolumePerMonth: 1000000,
    maxMerchants: 50,
    maxWallets: 200,
    maxApiCallsPerMonth: 100000,
    maxUsersPerAccount: 10,
    features: {
      customBranding: true,
      webhooks: true,
      advancedAnalytics: true,
      dedicatedSupport: false,
      slaGuarantee: false,
      customIntegrations: false
    }
  },
  enterprise: {
    maxTransactionsPerMonth: 100000,
    maxVolumePerMonth: 10000000,
    maxMerchants: 500,
    maxWallets: 2000,
    maxApiCallsPerMonth: 1000000,
    maxUsersPerAccount: 50,
    features: {
      customBranding: true,
      webhooks: true,
      advancedAnalytics: true,
      dedicatedSupport: true,
      slaGuarantee: true,
      customIntegrations: true
    }
  },
  custom: {
    maxTransactionsPerMonth: 1000000,
    maxVolumePerMonth: 100000000,
    maxMerchants: 10000,
    maxWallets: 50000,
    maxApiCallsPerMonth: 10000000,
    maxUsersPerAccount: 200,
    features: {
      customBranding: true,
      webhooks: true,
      advancedAnalytics: true,
      dedicatedSupport: true,
      slaGuarantee: true,
      customIntegrations: true
    }
  }
}

/**
 * Generate account billing info
 */
function generateBilling(plan: SubscriptionPlan): AccountBilling {
  const planPrices: Record<SubscriptionPlan, number> = {
    starter: 99,
    professional: 299,
    enterprise: 999,
    custom: 2500
  }

  const now = new Date()
  const nextBilling = new Date(now.getFullYear(), now.getMonth() + 1, 1)

  return {
    plan,
    billingCycle: 'monthly',
    nextBillingDate: nextBilling.toISOString(),
    amount: planPrices[plan],
    currency: 'USD',
    paymentMethod: {
      type: 'card',
      last4: faker.finance.creditCardNumber().slice(-4),
      brand: faker.helpers.arrayElement(['Visa', 'MasterCard', 'Amex']),
      expiryMonth: faker.number.int({ min: 1, max: 12 }),
      expiryYear: faker.number.int({ min: 2025, max: 2030 })
    }
  }
}

/**
 * Generate usage based on actual data
 */
function generateUsage(
  stats: AccountStatistics,
  limits: AccountLimits
): AccountUsage {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  return {
    currentPeriod: {
      start: startOfMonth.toISOString(),
      end: endOfMonth.toISOString()
    },
    transactions: {
      count: stats.totalTransactions,
      limit: limits.maxTransactionsPerMonth,
      percentage: Math.min(100, (stats.totalTransactions / limits.maxTransactionsPerMonth) * 100)
    },
    volume: {
      amount: stats.totalVolume,
      limit: limits.maxVolumePerMonth,
      percentage: Math.min(100, (stats.totalVolume / limits.maxVolumePerMonth) * 100),
      currency: 'USD'
    },
    merchants: {
      count: stats.totalMerchants,
      limit: limits.maxMerchants,
      percentage: Math.min(100, (stats.totalMerchants / limits.maxMerchants) * 100)
    },
    wallets: {
      count: stats.totalWallets,
      limit: limits.maxWallets,
      percentage: Math.min(100, (stats.totalWallets / limits.maxWallets) * 100)
    },
    apiCalls: {
      count: faker.number.int({ min: 5000, max: 50000 }),
      limit: limits.maxApiCallsPerMonth,
      percentage: faker.number.float({ min: 10, max: 60 })
    }
  }
}

/**
 * Generate API keys
 */
function generateApiKeys(): AccountApiKey[] {
  faker.seed(ACCOUNT_CONFIG.SEED + 100)

  return [
    {
      id: `key_${faker.string.alphanumeric(12)}`,
      name: 'Production API Key',
      keyPrefix: `pk_live_${faker.string.alphanumeric(4)}`,
      status: 'active',
      permissions: ['transactions:read', 'transactions:write', 'merchants:read', 'wallets:read'],
      lastUsed: new Date(Date.now() - faker.number.int({ min: 0, max: 2 }) * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `key_${faker.string.alphanumeric(12)}`,
      name: 'Test API Key',
      keyPrefix: `pk_test_${faker.string.alphanumeric(4)}`,
      status: 'active',
      permissions: ['transactions:read', 'transactions:write', 'merchants:read', 'wallets:read'],
      lastUsed: new Date(Date.now() - faker.number.int({ min: 0, max: 24 }) * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `key_${faker.string.alphanumeric(12)}`,
      name: 'Read-Only Key',
      keyPrefix: `pk_ro_${faker.string.alphanumeric(4)}`,
      status: 'active',
      permissions: ['transactions:read', 'merchants:read', 'wallets:read', 'settlements:read'],
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
    }
  ]
}

/**
 * Generate team members
 */
function generateTeamMembers(): AccountTeamMember[] {
  faker.seed(ACCOUNT_CONFIG.SEED + 200)

  return [
    {
      id: `user_${faker.string.alphanumeric(12)}`,
      name: faker.person.fullName(),
      email: 'owner@example.com',
      role: 'owner',
      status: 'active',
      lastLogin: new Date(Date.now() - faker.number.int({ min: 0, max: 4 }) * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `user_${faker.string.alphanumeric(12)}`,
      name: faker.person.fullName(),
      email: 'admin@example.com',
      role: 'admin',
      status: 'active',
      lastLogin: new Date(Date.now() - faker.number.int({ min: 0, max: 12 }) * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `user_${faker.string.alphanumeric(12)}`,
      name: faker.person.fullName(),
      email: 'manager@example.com',
      role: 'manager',
      status: 'active',
      lastLogin: new Date(Date.now() - faker.number.int({ min: 12, max: 48 }) * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `user_${faker.string.alphanumeric(12)}`,
      name: faker.person.fullName(),
      email: faker.internet.email(),
      role: 'viewer',
      status: 'pending',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
    }
  ]
}

/**
 * Generate integrations
 */
function generateIntegrations(): AccountIntegration[] {
  faker.seed(ACCOUNT_CONFIG.SEED + 300)

  return [
    {
      id: `int_${faker.string.alphanumeric(12)}`,
      name: 'Webhook - Transactions',
      type: 'webhook',
      status: 'active',
      endpoint: 'https://api.example.com/webhooks/transactions',
      lastSync: new Date(Date.now() - faker.number.int({ min: 0, max: 2 }) * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `int_${faker.string.alphanumeric(12)}`,
      name: 'Webhook - Settlements',
      type: 'webhook',
      status: 'active',
      endpoint: 'https://api.example.com/webhooks/settlements',
      lastSync: new Date(Date.now() - faker.number.int({ min: 0, max: 6 }) * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: `int_${faker.string.alphanumeric(12)}`,
      name: 'Slack Notifications',
      type: 'plugin',
      status: 'active',
      lastSync: new Date(Date.now() - faker.number.int({ min: 0, max: 1 }) * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
    }
  ]
}

/**
 * Generate compliance info
 */
function generateCompliance(): AccountCompliance {
  faker.seed(ACCOUNT_CONFIG.SEED + 400)

  const now = new Date()
  const kycApproved = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000)
  const nextReview = new Date(now.getTime() + 180 * 24 * 60 * 60 * 1000)

  return {
    kycStatus: 'approved',
    kycSubmittedAt: new Date(kycApproved.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    kycApprovedAt: kycApproved.toISOString(),
    amlStatus: 'clear',
    riskLevel: 'low',
    documentsRequired: ['business_registration', 'tax_id', 'bank_statement', 'owner_id'],
    documentsSubmitted: ['business_registration', 'tax_id', 'bank_statement', 'owner_id'],
    lastReviewDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    nextReviewDate: nextReview.toISOString()
  }
}

/**
 * Generate account settings
 */
function generateSettings(): AccountSettings {
  return {
    timezone: 'America/Sao_Paulo',
    language: 'en',
    currency: 'USD',
    notificationPreferences: {
      email: true,
      sms: true,
      push: true,
      settlementAlerts: true,
      transactionAlerts: false,
      securityAlerts: true
    },
    securitySettings: {
      twoFactorEnabled: true,
      ipWhitelist: [],
      sessionTimeout: 3600
    }
  }
}

/**
 * Generate account data based on statistics from mock data
 */
export function generateAccount(stats: AccountStatistics): Account {
  faker.seed(ACCOUNT_CONFIG.SEED)

  const plan = ACCOUNT_CONFIG.PLAN
  const limits = PLAN_LIMITS[plan]

  const now = new Date()
  const createdAt = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)

  return {
    id: `acct_${faker.string.alphanumeric(12)}`,
    accountUuid: faker.string.uuid(),

    companyName: 'Demo Company Inc.',
    legalName: 'Demo Company Incorporated',
    tradingName: 'Demo Co.',
    taxId: faker.string.numeric(14),
    registrationNumber: faker.string.alphanumeric(12).toUpperCase(),

    status: 'active',
    tier: 'premium',

    primaryEmail: 'contact@democompany.com',
    supportEmail: 'support@democompany.com',
    phone: faker.phone.number(),
    website: 'https://www.democompany.com',

    address: {
      street: faker.location.streetAddress(),
      city: faker.location.city(),
      state: faker.location.state({ abbreviated: true }),
      postalCode: faker.location.zipCode(),
      country: 'BR'
    },

    subscription: generateBilling(plan),
    usage: generateUsage(stats, limits),
    limits,

    apiKeys: generateApiKeys(),
    teamMembers: generateTeamMembers(),
    integrations: generateIntegrations(),

    compliance: generateCompliance(),
    settings: generateSettings(),

    createdAt: createdAt.toISOString(),
    updatedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    lastLoginAt: new Date(now.getTime() - 30 * 60 * 1000).toISOString()
  }
}

/**
 * Calculate account statistics from mock data
 */
export function calculateAccountStatistics(data: {
  merchantCount: number
  activeMerchantCount: number
  walletCount: number
  activeWalletCount: number
  transactionCount: number
  totalVolume: number
  successRate: number
  settlementCount: number
  pendingSettlementCount: number
  totalFees: number
}): AccountStatistics {
  return {
    totalMerchants: data.merchantCount,
    activeMerchants: data.activeMerchantCount,
    totalWallets: data.walletCount,
    activeWallets: data.activeWalletCount,
    totalTransactions: data.transactionCount,
    totalVolume: data.totalVolume,
    totalSettlements: data.settlementCount,
    pendingSettlements: data.pendingSettlementCount,
    totalFees: data.totalFees,
    averageTransactionValue: data.transactionCount > 0
      ? Number((data.totalVolume / data.transactionCount).toFixed(2))
      : 0,
    successRate: data.successRate
  }
}
